import { useCallback } from "react";

import {
  PolicyDocument,
  useGetPolicyWithDocumentsLazyQuery,
  useRemoveDocumentFromPolicyMutation,
} from "@covertree/agent-portal-graphql";
import { Toast } from "@covertree/utils";
import { useAccessPattern } from "@hooks/useAccessPattern";
import { isDocumentHidden } from "@utils/isDocumentHidden";
import environment from "@environment/variables";

export function useDocumentsActions(policyLocator: string) {
  const { canSeeInspectionOrderingDocument, canRemovePolicyDocument } =
    useAccessPattern();

  const [getPolicy, { loading: isPolicyRefreshing }] =
    useGetPolicyWithDocumentsLazyQuery();

  const [removeDocumentFromPolicy, { loading: isRemovePolicyLoading }] =
    useRemoveDocumentFromPolicyMutation();

  const isDocumentRemovable = useCallback(
    ({ displayName }: PolicyDocument) =>
      canRemovePolicyDocument &&
      displayName.includes(environment.customDocumentSuffix),
    [canRemovePolicyDocument]
  );

  const filterDocumentsToDisplay = useCallback(
    ({ displayName }: PolicyDocument) =>
      !isDocumentHidden(displayName, canSeeInspectionOrderingDocument),
    [canSeeInspectionOrderingDocument]
  );

  const onDocumentDownload = useCallback(
    async (document: PolicyDocument) => {
      if (parseInt(document.urlExpirationTimestamp) >= Date.now() - 2000) {
        window.open(document.url, "_blank");
      } else {
        Toast.showInfo("Refreshing documents");

        await getPolicy({
          fetchPolicy: "cache-and-network",
          variables: { policyLocator },
          onCompleted: (data) => {
            if (!data || !data.getPolicy.documents) {
              Toast.showError("Couldn't refresh documents");
              return;
            }
            const refreshedDocument = data.getPolicy.documents.find(
              (doc) => doc.documentLocator === document.documentLocator
            );
            window.open(refreshedDocument.url, "_blank");
          },
        });
      }
    },
    [policyLocator]
  );

  const onValuationReportDownload = useCallback(
    (valuationReportUrl: string) => {
      if (valuationReportUrl) {
        window.open(valuationReportUrl, "_blank");
      }
    },
    []
  );

  const onDocumentRemove = useCallback(
    async ({ documentLocator }: PolicyDocument) => {
      try {
        await removeDocumentFromPolicy({
          variables: {
            documentLocator,
            policyLocator,
          },
        });
        getPolicy({ variables: { policyLocator } });
        Toast.showSuccess("Document removed successfully");
      } catch (e) {
        Toast.showError("Couldn't remove document");
      }
    },
    [policyLocator]
  );

  return {
    isPolicyRefreshing,
    isRemovePolicyLoading,
    onDocumentDownload,
    onValuationReportDownload,
    onDocumentRemove,
    filterDocumentsToDisplay,
    isDocumentRemovable,
  };
}
